import React, { useMemo, useState, useEffect, useRef } from 'react';
// ColumnInstance might not be explicitly needed if Column is sufficient for typing
import { useTable, useSortBy, usePagination, Column, HeaderGroup } from 'react-table'; 

// Types
import { InstanceDisplayRow, ClientTagColumnDefinition } from './Tags'; // Import from Tags.tsx
// import TagsType from '../../../types/Tags'; // Not directly used here, InstanceDisplayRow has _originalTags

// Components
import NoData from '../../../component/NoData';
import TablePagination from '../../../component/TablePagination';
import SortClass from '../../../component/SortClass';
import Alert from '../../../component/Alert';
import { getDefaultPageSize } from '../../../helpers/utils';

// Styles
import './tags.scss';

// Helper for editable cell
const EditableCell = ({
  value: initialValue,
  row: { original },
  column: { id },
  onCellEdit,
  getUniqueTagValuesForColumn,
  isAdmin,
}: { value: any, row: { original: InstanceDisplayRow }, column: { id: string }, onCellEdit: Function, getUniqueTagValuesForColumn: Function, isAdmin: boolean }) => {
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const uniqueValues = useMemo(() => getUniqueTagValuesForColumn(id), [getUniqueTagValuesForColumn, id]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const onBlur = (e: React.FocusEvent) => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    // Use setTimeout to allow click events to fire before closing dropdown
    blurTimeoutRef.current = setTimeout(() => {
      // Check if the related target is inside our dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget as Node)) {
        return; // Don't close if clicking inside dropdown
      }
      setIsEditing(false);
      setShowDropdown(false);
      if (initialValue !== value) {
        onCellEdit(original.instanceId, id, value === '' ? null : value);
      }
    }, 100);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (value !== null) {
      onCellEdit(original.instanceId, id, null);
      setValue(null);
    }
  };

  const handleDropdownSelect = (selectedValue: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    setValue(selectedValue);
    onCellEdit(original.instanceId, id, selectedValue);
    setShowDropdown(false);
    setIsEditing(false);
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  if (isEditing) {
    return (
      <div className="position-relative" onBlur={onBlur}>
        <input
          ref={inputRef}
          value={value || ''}
          onChange={onChange}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          className="form-control form-control-sm editable-cell-input"
        />
        {showDropdown && uniqueValues.length > 0 && (
          <div 
            ref={dropdownRef} 
            className="editable-cell-dropdown show"
            onClick={handleDropdownClick}
          >
            {uniqueValues.map((val: string, index: number) => (
              <button
                key={index}
                className={`dropdown-item ${val === value ? 'active' : ''}`}
                onClick={(e) => handleDropdownSelect(val, e)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleDropdownSelect(val, e);
                }}
                type="button"
              >
                {val}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => isAdmin && setIsEditing(true)}
      className={`editable-cell-display ${!isAdmin ? 'read-only' : ''}`}
    >
      {value || <span className="text-muted">&nbsp;</span>}
      {isAdmin && value ? (
        <button
          className="clear-button"
          onClick={handleClear}
          title="Clear value"
        >
          <i className="fal fa-times fa-fw" />
        </button>
      ) : (
        isAdmin && <div className="dropdown-arrow" />
      )}
    </div>
  );
};

interface TagListProps {
  data: InstanceDisplayRow[];
  columns: ClientTagColumnDefinition[];
  loadingState: boolean;
  onCellEdit: (instanceId: number, tagName: string, newValue: string | null) => void;
  onNewColumn: (newColumnName: string) => void;
  getUniqueTagValuesForColumn: (tagName: string) => string[]; // To populate datalist
  handleSaveChanges: () => Promise<void>; // Added prop for save function
  isSaving: boolean; // Added prop to disable button while saving
  hasUnsavedChanges: boolean; // Added prop to track unsaved changes
  isAdmin: boolean; // Added prop for admin access control
}

const TagList: React.FC<TagListProps> = ({
  data,
  columns: columnDefs, // Renaming to avoid conflict with react-table's columns
  loadingState,
  onCellEdit,
  onNewColumn,
  getUniqueTagValuesForColumn,
  handleSaveChanges, // Destructure new prop
  isSaving, // Destructure new prop
  hasUnsavedChanges, // Destructure new prop
  isAdmin, // Destructure new prop
}) => {
  const [newTagName, setNewTagName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAddNewColumn = () => {
    if (newTagName.trim() && !columnDefs.find(col => col.name === newTagName.trim())) {
      onNewColumn(newTagName.trim());
      setNewTagName('');
      setErrorMessage(null);
    } else {
      setErrorMessage("Tag name already exist.");
    }
  };
  
  // Calculate table min-width based on number of tags
  const tableMinWidth = useMemo(() => {
    const instanceColumnWidth = 200; // Instance column width
    const tagColumnWidth = 150; // Each tag column width
    const totalTagColumnsWidth = columnDefs.length * tagColumnWidth;
    return instanceColumnWidth + totalTagColumnsWidth;
  }, [columnDefs.length]);
  
  const tableColumns = useMemo<Column<InstanceDisplayRow>[]>(() => {
    const staticColumns: Column<InstanceDisplayRow>[] = [
      {
        Header: 'Instance',
        accessor: 'instanceName',
        // disableSortBy: true, // Removed to enable sorting
        width: 200, // Example width for Instance column
        sortType: (rowA, rowB, columnId) => {
          const a = String(rowA.values[columnId]).toLowerCase();
          const b = String(rowB.values[columnId]).toLowerCase();
          if (a > b) return 1;
          if (b > a) return -1;
          return 0;
        },
      },
    ];

    const dynamicColumns: Column<InstanceDisplayRow>[] = columnDefs.map(colDef => ({
      Header: colDef.name,
      accessor: colDef.name as string, // Accessor must be a string or function
      width: 150, // Example default width for tag columns
      Cell: (cellProps: any) => ( // cellProps needs typing
        <EditableCell
          {...cellProps}
          onCellEdit={onCellEdit}
          getUniqueTagValuesForColumn={getUniqueTagValuesForColumn}
          isAdmin={isAdmin}
        />
      ),
    }));

    return [...staticColumns, ...dynamicColumns];
  }, [columnDefs, onCellEdit, getUniqueTagValuesForColumn]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: tableColumns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: getDefaultPageSize(),
      },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
      autoResetGroupBy: false,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="card tag-list">
      <div className="card-header">
        <i className="fal fa-tags fa-fw" aria-hidden="true" /> Instance Tags
        <span className="badge bg-info ms-2" data-tip="Total instances count">
          {data.length}
        </span>
        <i 
          className="collapse-toggle" 
          role="button" 
          data-bs-toggle="collapse"
          data-bs-target="#collapseInstanceTags" 
          aria-expanded="false" 
          aria-controls="collapseInstanceTags"
        />
      </div>
      <div id="collapseInstanceTags" className="card-body collapse show">
        {errorMessage && (
          <Alert 
            message={errorMessage}
            heading="Invalid Tag Name" 
            variant="alert-danger"
          />
        )}
        <div className="row">
          <div className="col col-md-12 text-end tag-controls">
            {isAdmin && (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm d-inline-block me-2 tag-name-input"
                  placeholder="New Tag Name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleAddNewColumn();}}
                  data-lpignore={true}
                  autoFocus
                />

                <button
                        className='btn btn-sm btn-primary me-2'
                        onClick={handleAddNewColumn}
                        disabled={!newTagName}
                        title="Create Tag"
                >
                  <i className="fal fa-save fa-fw" /> Create Tag
                </button>

                <button
                  className='btn btn-sm btn-success'
                  onClick={handleSaveChanges}
                  disabled={!hasUnsavedChanges || isSaving || loadingState}
                  title="Save all changes"
                >
                  <i className="fal fa-save fa-fw" /> Save Changes
                </button>
              </>
            )}
          </div>
        </div>
        <div className="table-responsive">
          <table 
            {...getTableProps()} 
            className="table tag-table"
            style={{ minWidth: `${tableMinWidth}px` }}
          >
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<InstanceDisplayRow>) => ( // Type headerGroup
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => { // column is already ColumnInstance<InstanceDisplayRow> here
                    const thProps = {...column.getHeaderProps(column.getSortByToggleProps())};
                    // Removed sticky-column logic
                    return (
                      <th {...thProps} className="tag-header-cell">
                        <div className="tag-header-content" title={column.render('Header') as string}>
                          {column.render('Header')}
                          {column.canSort ? <SortClass column={column as any} /> : ''}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      const tdProps = {...cell.getCellProps()};
                      // Removed sticky-column logic
                      return (
                        <td {...tdProps}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <NoData loading={loadingState} length={data.length} error={null} />
          <TablePagination
            length={data.length}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageOptions={pageOptions}
            pageCount={pageCount}
            canPreviousPage={canPreviousPage}
            previousPage={previousPage}
            canNextPage={canNextPage}
            nextPage={nextPage}
            gotoPage={gotoPage}
            pageIndex={pageIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default TagList;
